import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { fmt, today } from "../utils";
import UnitInput from "../components/UnitInput";

const emptyLine = { item_id: "", item_name: "", description: "", qty: 1, rate: "", discount_pct: 0, gst_rate: 0, hsn: "", unit: "", supply_type: null };

export default function QuotationForm() {
  const [hospitals, setHospitals] = useState([]);
  const [items, setItems] = useState([]);
  const [hospitalId, setHospitalId] = useState("");
  const [addressee, setAddressee] = useState("");
  const [letterRef, setLetterRef] = useState("");
  const [tenderRef, setTenderRef] = useState("");
  const [validTill, setValidTill] = useState("");
  const [date, setDate] = useState(today());
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const requestKey = useRef(null);
  const nav = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const copyFrom = searchParams.get("copy_from") || "";
  const editing = Boolean(id);
  const duplicating = !editing && Boolean(copyFrom);

  useEffect(() => {
    api.get("/hospitals").then((r) => setHospitals(r.data)).catch(() => {});
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
    const sourceId = id || copyFrom;
    if (sourceId) {
      api.get(`/quotations/${sourceId}`).then(({ data: q }) => {
        setHospitalId(q.hospital_id || "");
        setAddressee(q.addressee || "");
        setLetterRef(q.letter_ref || "");
        setTenderRef(q.tender_ref || "");
        setValidTill(q.valid_till || "");
        setDate(id ? (q.date || today()) : today());
        setLines((q.items || []).map((line) => ({
          item_id: line.item_id || "",
          item_name: line.item_name || "",
          description: line.description || "",
          qty: line.qty ?? 1,
          rate: line.rate ?? "",
          discount_pct: line.discount_pct ?? 0,
          gst_rate: line.gst_rate ?? 0,
          hsn: line.hsn || "",
          unit: line.unit || "",
          supply_type: line.supply_type || null,
        })));
      }).catch((err) => {
        toast.error(err.response?.data?.detail || err.message || "Quotation load nahi ho paaya");
        nav("/quotations", { replace: true });
      });
    }
  }, [id, copyFrom, nav]);

  const hospital = hospitals.find((h) => h.id === hospitalId);
  const interstate = hospital?.gstin && hospital.gstin.slice(0, 2) !== "10";
  const setLine = (i, patch) => {
    const next = [...lines];
    next[i] = { ...next[i], ...patch };
    setLines(next);
  };

  const totals = useMemo(() => {
    let taxable = 0, tax = 0;
    lines.forEach((l) => {
      if (!l.item_name?.trim()) return;
      const base = (Number(l.qty) || 0) * (Number(l.rate) || 0);
      const t = base - base * (Number(l.discount_pct) || 0) / 100;
      taxable += t;
      tax += t * (Number(l.gst_rate) || 0) / 100;
    });
    return { taxable, grand: Math.round(taxable + tax) };
  }, [lines]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    const started = lines.filter((l) => l.item_name?.trim() || l.rate !== "" || l.unit || l.hsn || l.description?.trim());
    const valid = started.filter((l) => l.item_name?.trim() && Number(l.qty) > 0 && l.rate !== "" && Number(l.rate) >= 0);
    if (!hospitalId) return toast.error("Hospital select karo");
    if (validTill && validTill < date) return toast.error("Valid Till quotation date se pehle nahi ho sakta");
    if (!valid.length) return toast.error("Kam se kam ek item/service ka naam aur rate add karo");
    if (valid.length !== started.length) return toast.error("Kisi quotation row me naam, quantity ya rate incomplete hai — row complete ya delete karo");
    if (valid.some((l) => Number(l.discount_pct || 0) < 0 || Number(l.discount_pct || 0) > 100 || Number(l.gst_rate || 0) < 0 || Number(l.gst_rate || 0) > 100))
      return toast.error("Discount aur GST 0–100% ke beech hona chahiye");
    submitting.current = true;
    setSaving(true);
    try {
      const payload = {
        hospital_id: hospitalId, date, valid_till: validTill,
        addressee, letter_ref: letterRef, tender_ref: tenderRef,
        items: valid.map((l) => ({
          item_id: l.item_id || null,
          item_name: l.item_name.trim(),
          description: (l.description || "").trim(),
          qty: Number(l.qty),
          rate: Number(l.rate),
          discount_pct: Number(l.discount_pct) || 0,
          gst_rate: Number(l.gst_rate) || 0,
          hsn: (l.hsn || "").trim(),
          unit: (l.unit || "").trim(),
          supply_type: l.supply_type || null,
        })),
      };
      const fingerprint = JSON.stringify(payload);
      if (requestKey.current?.fingerprint !== fingerprint) {
        requestKey.current = { fingerprint, key: crypto.randomUUID() };
      }
      const q = (await (editing
        ? api.put(`/quotations/${id}`, payload)
        : api.post("/quotations", payload, { headers: { "Idempotency-Key": requestKey.current.key } }))).data;
      toast.success(editing ? `Quotation ${q.quote_no} updated` : `Quotation ${q.quote_no} saved`);
      nav("/quotations");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Quotation save nahi ho paaya");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <form data-testid="quotation-form" onSubmit={submit} className="sgp-page max-w-6xl">
      <div className="sgp-page-header">
        <div>
          <h1 className="sgp-page-title">{editing ? "Edit Quotation" : duplicating ? "Duplicate Quotation" : "New Quotation"}</h1>
          {editing && <p className="sgp-page-subtitle">Quotation number same rahega; changes audit trail me save honge.</p>}
          {duplicating && <p className="sgp-page-subtitle">Old quotation copy hua hai; save karne par naya quotation number banega.</p>}
        </div>
        <button type="submit" data-testid="quotation-save-btn" disabled={saving}
          className="sgp-primary-btn px-5 disabled:opacity-50 disabled:pointer-events-none">
          {saving ? "Saving..." : editing ? "Save Changes" : duplicating ? "Create Copy" : "Save Quotation"}
        </button>
      </div>

      <div className="sgp-card p-5 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="sm:col-span-2">
          <label className="sgp-label">Hospital / Institution *</label>
          <select data-testid="quotation-hospital-select" required className="sgp-input" value={hospitalId} onChange={(e) => setHospitalId(e.target.value)}>
            <option value="">— Select from master —</option>
            {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div>
          <label className="sgp-label">Date</label>
          <input data-testid="quotation-date" type="date" className="sgp-input font-mono" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="sgp-label">Valid Till</label>
          <input data-testid="quotation-valid-till" type="date" className="sgp-input font-mono" value={validTill} onChange={(e) => setValidTill(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="sgp-label">Tender / Enquiry Ref.</label>
          <input data-testid="quotation-tender-ref" className="sgp-input font-mono" placeholder="e.g. BMSICL/ENQ/2026/104" value={tenderRef} onChange={(e) => setTenderRef(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="sgp-label">To (e.g. The Civil Surgeon Cum Member Secretary, DHS Madhubani)</label>
          <input data-testid="quotation-addressee" className="sgp-input" value={addressee} onChange={(e) => setAddressee(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="sgp-label">Ref. Letter / Gyapank No. & Date</label>
          <input data-testid="quotation-letter-ref" className="sgp-input font-mono" placeholder="1002/DHS/Madhubani, Dt. 06-06-2026" value={letterRef} onChange={(e) => setLetterRef(e.target.value)} />
        </div>
        {hospital && <p className="sm:col-span-2 text-xs text-slate-500 self-end">GSTIN: <span className="font-mono">{hospital.gstin || "Unregistered"}</span> · {interstate ? "IGST" : "CGST+SGST"}</p>}
      </div>

      <datalist id="quotation-item-suggestions">
        {items.map((x) => <option key={x.id} value={x.name} />)}
      </datalist>

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr>
              <th className="sgp-th min-w-[320px]">Item / Service Name</th>
              <th className="sgp-th">Unit / Type</th>
              <th className="sgp-th text-right">Qty</th>
              <th className="sgp-th text-right">Quoted Rate</th>
              <th className="sgp-th text-right">Disc%</th>
              <th className="sgp-th text-right">GST%</th>
              <th className="sgp-th text-right">Amount</th>
              <th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const base = (Number(l.qty) || 0) * (Number(l.rate) || 0);
              const amt = base - base * (Number(l.discount_pct) || 0) / 100;
              return (
                <tr key={i} data-testid={`quotation-line-${i}`}>
                  <td className="sgp-td">
                    <input
                      data-testid={`quotation-item-name-${i}`}
                      list="quotation-item-suggestions"
                      className="sgp-input"
                      placeholder="Type medicine, equipment or service name"
                      value={l.item_name}
                      onChange={(e) => {
                        const value = e.target.value;
                        const match = items.find((x) => x.name.trim().toLowerCase() === value.trim().toLowerCase());
                        setLine(i, {
                          item_name: value,
                          item_id: match?.id || "",
                          hsn: match?.hsn || (match ? "" : l.hsn),
                          unit: match?.unit || (match ? "" : l.unit),
                          gst_rate: match ? Number(match.gst_rate || 0) : l.item_id ? 0 : l.gst_rate,
                          supply_type: match?.supply_type || null,
                        });
                      }}
                    />
                    <div className="mt-1.5 grid grid-cols-[110px_1fr] gap-1.5">
                      <input data-testid={`quotation-hsn-${i}`} className="sgp-input h-8 font-mono text-xs" placeholder="HSN (optional)" value={l.hsn || ""} onChange={(e) => setLine(i, { hsn: e.target.value })} />
                      <input data-testid={`quotation-description-${i}`} className="sgp-input h-8 text-xs" placeholder="Description / specification (optional)" value={l.description || ""} onChange={(e) => setLine(i, { description: e.target.value })} />
                    </div>
                    {l.item_id && <p className="mt-1 text-[10px] font-medium text-emerald-700">Master matched only for reference — quotation stock reserve/check nahi karti.</p>}
                  </td>
                  <td className="sgp-td">
                    <UnitInput testId={`quotation-unit-${i}`} value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} className="sgp-input w-28" />
                  </td>
                  <td className="sgp-td"><input data-testid={`quotation-qty-${i}`} type="number" step="0.001" min="0.001" className="sgp-input font-mono w-20 text-right" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`quotation-rate-${i}`} type="number" step="0.01" min="0" className="sgp-input font-mono w-28 text-right" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} placeholder="0.00" /></td>
                  <td className="sgp-td"><input data-testid={`quotation-disc-${i}`} type="number" step="0.01" min="0" max="100" className="sgp-input font-mono w-20 text-right" value={l.discount_pct} onChange={(e) => setLine(i, { discount_pct: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`quotation-gst-${i}`} type="number" step="0.01" min="0" max="100" className="sgp-input font-mono w-20 text-right" value={l.gst_rate} onChange={(e) => setLine(i, { gst_rate: e.target.value })} /></td>
                  <td className="sgp-td text-right font-mono text-xs">{l.item_name?.trim() ? fmt(amt) : "-"}</td>
                  <td className="sgp-td">
                    <button type="button" data-testid={`quotation-line-del-${i}`} onClick={() => setLines(lines.length === 1 ? [{ ...emptyLine }] : lines.filter((_, j) => j !== i))}
                      className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="p-3 border-t border-slate-100">
          <button type="button" data-testid="quotation-add-line" onClick={() => setLines([...lines, { ...emptyLine }])}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-dashed border-slate-300 text-slate-600 hover:border-teal-600 hover:text-teal-700">
            <Plus className="h-3.5 w-3.5" /> Add Quotation Row
          </button>
        </div>
      </div>

      <div className="sgp-card p-5 ml-auto max-w-sm space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Taxable Amount</span><span data-testid="quotation-taxable" className="font-mono">{fmt(totals.taxable)}</span></div>
        <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
          <span>Approx. Total (incl. GST)</span><span data-testid="quotation-grand-total" className="font-mono text-teal-700">{fmt(totals.grand)}</span>
        </div>
      </div>
    </form>
  );
}
